import { Component, OnInit, ViewEncapsulation, Input, Output, EventEmitter } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { AppPrice, Fee, FeeAmountType } from '@vendasta/marketplace-apps';
import { getFeesForEdition, calculateFeeAmount, getSetupFee } from '../utils/price-display';
import { labelForGalaxyFrequency, toGalaxyFrequency } from '../utils/frequency';
import { convertCentsToDollars } from '@vendasta/shared';

const NOTE_MAX_LENGTH = 150;

export const baseTranslationKey = 'PRICING_COMMONS.';
export const managementFeeTranslationKey = 'MANAGEMENT_FEE_LABEL';
export const setupFeeTranslationKey = 'SETUP_FEE_LABEL';
export const variablePriceInputTranslationKey = 'VARIABLE_PRICE_INPUT.';

@Component({
  selector: 'billing-ui-variable-price-input',
  templateUrl: './variable-price-input.component.html',
  styleUrls: ['./variable-price-input.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class VariablePriceInputComponent implements OnInit {
  FeeAmountType = FeeAmountType;

  @Input() appPrice: AppPrice;
  @Input() currencyCode = 'USD';
  @Input() displayAddNote = false;
  @Input() currentSpendAmount = 0;
  @Input() disabled = false;
  @Input() displaySetupFee = true;

  @Output() spendAmountChanged = new EventEmitter<number>();
  @Output() requesterNoteChanged = new EventEmitter<string>();

  formGroup: UntypedFormGroup;
  minSpendAmount: number;
  minSpendAmountDisplay: number;
  displayFrequency: string;

  addingNote = false;

  noteMaxLength = NOTE_MAX_LENGTH;

  fees: Fee[];
  managementFee: Fee;
  setupFee: number;
  calculatedManagementFeeAmount$$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  managementFeeLabel = baseTranslationKey + managementFeeTranslationKey;
  setupFeeLabel = baseTranslationKey + setupFeeTranslationKey;

  inputLabelKey = baseTranslationKey + variablePriceInputTranslationKey + 'LABEL';
  addNoteKey = baseTranslationKey + variablePriceInputTranslationKey + 'ADD_NOTE';
  noteLabelKey = baseTranslationKey + variablePriceInputTranslationKey + 'NOTE_LABEL';

  constructor(private formBuilder: UntypedFormBuilder) {}

  ngOnInit(): void {
    // Set the default value to 0 in case the input value is undefined
    this.currentSpendAmount = this.currentSpendAmount ?? 0;
    this.minSpendAmount =
      this.appPrice.pricesForEditions[''].pricesForCurrencies[this.currencyCode].pricesForFrequencies[0].pricingRules[0]
        .price ?? 0;

    this.minSpendAmountDisplay = convertCentsToDollars(this.minSpendAmount);

    this.formGroup = this.formBuilder.group({
      spendAmount: [
        {
          value: (this.currentSpendAmount
            ? convertCentsToDollars(this.currentSpendAmount)
            : this.minSpendAmountDisplay
          ).toFixed(2),
          disabled: this.disabled,
        },
        [Validators.required, Validators.min(this.minSpendAmountDisplay)],
      ],
      requesterNote: ['', [Validators.maxLength(NOTE_MAX_LENGTH)]],
    });

    this.fees = getFeesForEdition(this.appPrice, this.currencyCode, '') || [];

    if (!!this.fees && this.fees.length > 0) {
      this.managementFee = this.fees[0];

      const spendAmount = this.currentSpendAmount ? this.currentSpendAmount : this.minSpendAmount;
      this.calculatedManagementFeeAmount$$.next(calculateFeeAmount(spendAmount, this.fees));
    }

    this.setupFee = getSetupFee(this.appPrice, this.currencyCode, '');

    this.displayFrequency = labelForGalaxyFrequency(
      toGalaxyFrequency(
        this.appPrice.pricesForEditions[''].pricesForCurrencies[this.currencyCode].pricesForFrequencies[0].frequency,
      ),
      true,
      true,
    );
  }

  addNote(): void {
    this.addingNote = true;
  }

  changeRequesterNote(event: any): void {
    this.requesterNoteChanged.emit(event.target.value);
  }

  changeSpendAmount(event: any): void {
    let val = event?.target?.value;
    if (!val) {
      return;
    }

    val *= 100;

    if (val && Number(val) >= this.minSpendAmount) {
      this.formGroup.controls['spendAmount'].setValue((val / 100).toFixed(2));
      this.spendAmountChanged.emit(val);
      this.calculatedManagementFeeAmount$$.next(calculateFeeAmount(val, this.fees));
    } else if (val && Number(val) < this.minSpendAmount) {
      //Set value to minimum if lower value is inputted
      this.formGroup.controls['spendAmount'].setValue(this.minSpendAmountDisplay.toFixed(2));
      this.spendAmountChanged.emit(this.minSpendAmount);
      this.calculatedManagementFeeAmount$$.next(calculateFeeAmount(this.minSpendAmount, this.fees));
    }
  }

  onKeyUpSpendAmount(event: any): void {
    let val = event?.target?.value;
    if (!val) {
      return;
    }
    val *= 100;
    this.calculatedManagementFeeAmount$$.next(calculateFeeAmount(val, this.fees));
  }
}
